import { usePanic } from '../contexts/PanicContext';
import { Button } from './ui/button';
import { X, Copy, Check, Shield, AlertTriangle, AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

function ScenarioSelector({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="panic-scenario-select" className="w-full">
        <SelectValue placeholder="Select scenario" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="workplace">Workplace</SelectItem>
        <SelectItem value="medical">Medical</SelectItem>
        <SelectItem value="travel">Travel</SelectItem>
        <SelectItem value="school">School</SelectItem>
        <SelectItem value="social">Social</SelectItem>
      </SelectContent>
    </Select>
  );
}

function RiskIndicator({ level }) {
  if (level === 'low') {
    return (
      <div className="flex items-center gap-2 px-4 py-2 rounded-full w-fit text-green-600 bg-green-100">
        <Shield className="h-4 w-4" />
        <span className="text-sm font-medium">Low Risk</span>
      </div>
    );
  }
  if (level === 'high') {
    return (
      <div className="flex items-center gap-2 px-4 py-2 rounded-full w-fit text-red-600 bg-red-100">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm font-medium">High Risk</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-4 py-2 rounded-full w-fit text-yellow-600 bg-yellow-100">
      <AlertTriangle className="h-4 w-4" />
      <span className="text-sm font-medium">Medium Risk</span>
    </div>
  );
}

function TipsList({ tips }) {
  if (!tips || tips.length === 0) return null;
  
  return (
    <div className="card-soft bg-secondary/50">
      <h3 className="font-semibold text-foreground mb-2">Right Now:</h3>
      <ul className="space-y-1 text-sm text-muted-foreground">
        <li className="flex items-start gap-2">
          <span className="text-primary">•</span>
          <span>{tips[0] || 'Stay calm'}</span>
        </li>
        {tips[1] && (
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>{tips[1]}</span>
          </li>
        )}
        {tips[2] && (
          <li className="flex items-start gap-2">
            <span className="text-primary">•</span>
            <span>{tips[2]}</span>
          </li>
        )}
      </ul>
    </div>
  );
}

export default function PanicOverlay() {
  const { 
    isPanicMode, 
    panicResult, 
    panicLoading, 
    selectedScenario,
    setSelectedScenario,
    triggerPanic,
    closePanic 
  } = usePanic();
  
  const [copiedQuick, setCopiedQuick] = useState(false);
  const [copiedAssertive, setCopiedAssertive] = useState(false);

  if (!isPanicMode) return null;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'quick') {
        setCopiedQuick(true);
        setTimeout(() => setCopiedQuick(false), 2000);
      } else {
        setCopiedAssertive(true);
        setTimeout(() => setCopiedAssertive(false), 2000);
      }
      toast.success('Copied!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleScenarioChange = (val) => {
    setSelectedScenario(val);
    triggerPanic(val);
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto"
      data-testid="panic-overlay"
    >
      <div className="min-h-screen pb-safe">
        <div className="max-w-lg mx-auto px-6 pt-8">
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl font-semibold text-accent">Panic Mode</h1>
            <button
              onClick={closePanic}
              className="p-2 hover:bg-muted rounded-full transition-colors"
              data-testid="close-panic-btn"
            >
              <X className="h-6 w-6 text-foreground" />
            </button>
          </div>

          <p className="mt-2 text-muted-foreground">Quick scripts when you need them now</p>

          <div className="mt-6">
            <label className="text-sm font-medium text-foreground mb-2 block">Situation Type</label>
            <ScenarioSelector value={selectedScenario} onChange={handleScenarioChange} />
          </div>

          {panicLoading && (
            <div className="mt-10 flex flex-col items-center justify-center py-12">
              <div className="animate-breathe">
                <Loader2 className="h-12 w-12 text-accent animate-spin" />
              </div>
              <p className="mt-4 text-muted-foreground">Generating your script...</p>
              <p className="mt-2 text-sm text-muted-foreground">Take a deep breath</p>
            </div>
          )}

          {!panicLoading && panicResult && (
            <div className="mt-6 space-y-4">
              <RiskIndicator level={panicResult.risk_level} />

              <div className="card-soft">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-semibold text-foreground">Quick Script</h2>
                  <Button
                    data-testid="copy-quick-script-btn"
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(panicResult.quick_script, 'quick')}
                  >
                    {copiedQuick ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
                    {copiedQuick ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <div className="bg-muted rounded-xl p-4">
                  <p className="text-foreground italic" data-testid="quick-script">
                    "{panicResult.quick_script}"
                  </p>
                </div>
              </div>

              <div className="card-soft border-accent/30">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-semibold text-foreground">More Direct</h2>
                  <Button
                    data-testid="copy-assertive-script-btn"
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(panicResult.assertive_version, 'assertive')}
                  >
                    {copiedAssertive ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
                    {copiedAssertive ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <div className="bg-accent/10 rounded-xl p-4">
                  <p className="text-foreground italic" data-testid="assertive-script">
                    "{panicResult.assertive_version}"
                  </p>
                </div>
              </div>

              <TipsList tips={panicResult.immediate_tips} />
            </div>
          )}

          <div className="mt-8 text-center">
            <div className="inline-block p-8 rounded-full bg-secondary/50 animate-breathe">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-primary/40" />
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Breathe with the circle. You've got this.
            </p>
          </div>

          <div className="mt-8 pb-8">
            <Button
              data-testid="close-panic-done-btn"
              onClick={closePanic}
              className="btn-secondary w-full"
            >
              I'm Ready - Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
